import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { onBeforeLogout, useAuth } from '../../lib/auth';
import { useI18n } from '../../lib/i18n';
import { rt, TAB_ID, type RtStatus } from '../../lib/rt';
import { SharePeer, advanceCursor, newCursor, type PeerAction, type PeerPhase, type PeerState, type SignalCursor } from '../../lib/share/peer';
import { CaptureFailed, captureScreen, displayMediaSupported, micConstraints, type Capture, type SystemAudioState } from '../../lib/share/capture';
import { armChime, playChime } from '../../lib/share/chime';
import { parseShareDeepLink, stripShareDeepLink } from '../../lib/share/deepLink';
import { docPipSupported, exitVideoPip, openDocPip, requestVideoPip, setVideoSource, type DocPipHandle } from '../../lib/share/pip';
import {
  LIVE_STATUSES, type AcceptResponse, type BroadcastDTO, type HandoffResponse, type InviteResponse, type ParticipantDTO, type PeerStats,
  type SessionDTO, type SignalDTO, type SignalKind, type StateResponse, type TargetRow,
} from '../../lib/share/types';
import { MAX_VIEWERS, planReconcile } from '../../lib/share/mesh';
import { ShareTargetPicker } from './ShareTargetPicker';
import { ShareRequestCard } from './ShareRequestCard';
import { ShareStage } from './ShareStage';
import { SharerPill } from './SharerPill';

/**
 * EL CEREBRO DEL SHARE EN VIVO, montado una vez en App.tsx envolviendo las rutas (sirve al shell de
 * oficina Y a la app del inspector, spec §4.11) y otra vez en la ventana propia `/share/view/:id`
 * con `windowSessionId`. Guarda la sesión, el peer, la captura y el estado del socket; pinta la
 * tarjeta de invitación, el selector, el visor y la píldora del sharer.
 *
 * Dos permisos distintos: `canShare` (office+ con `share_screen_enabled`) pinta el botón y el
 * selector; `canView` (`share_screen_enabled` o `share_view_enabled`: los inspectores) abre el
 * socket y recibe la tarjeta y el visor. El inspector ve y oye; nunca comparte.
 *
 * Fuente de verdad: Postgres vía REST (`api.ts`). El socket (`rt.ts`) solo despierta: cada cambio
 * llega como `share.update`/`share.signal` y, si el socket se cayó en el medio, `refreshState()` +
 * el replay de señales por `lastSignalId` reponen lo perdido. Mirar NO es actividad: acá no se
 * inventa ningún `x-idle-ms` ni se toca el reloj — el aviso de corte de sesión se calcula con lo que
 * manda el servidor en `/state`.
 */

export type Role = 'sharer' | 'viewer';

export type Notice =
  | { kind: 'declined' | 'noAnswer' | 'busy' | 'notEnabled' | 'offlineSent' | 'endedBy' | 'viewerLeft' | 'lost' | 'cancelledPicker' | 'gone' | 'answeredElsewhere' | 'popupBlocked' | 'micDenied' | 'noMic' | 'movedBack' | 'rateLimited' | 'notEnabledMe' | 'broadcastFull' | 'error'; name?: string; message?: string; again?: TargetRow; max?: number };

/**
 * Aviso a partir de un error del API. `ApiError.message` para una respuesta sin `message` es el
 * CÓDIGO (`rate_limited`, `share_not_enabled`, `http_502`): eso no se le muestra a nadie. Solo pasa
 * un mensaje si es legible (trae espacios: el texto de red de api.ts o un `message` del servidor);
 * si no, `noticeText` cae al genérico traducido. El 429 tiene su propia frase.
 */
export function errNotice(e: unknown): Notice {
  if (e instanceof ApiError) {
    if (e.status === 429) return { kind: 'rateLimited' };
    if (e.code === 'share_disabled' || e.code === 'share_not_enabled') return { kind: 'notEnabledMe' };
    if (e.message && /\s/.test(e.message)) return { kind: 'error', message: e.message };
  }
  return { kind: 'error' };
}

type View = {
  socket: RtStatus;
  socketReconnecting: boolean;
  pending: SessionDTO[];
  /** Vista del VIEWER: la sesión que estoy mirando. */
  active: SessionDTO | null;
  /** Vista del SHARER: mi broadcast (una captura, N viewers). El sharer NO usa `active`. */
  broadcast: BroadcastDTO | null;
  /** Sharer: fase del peer POR viewer (viewerId → fase), para el roster y "N watching". */
  peerPhases: Record<string, PeerPhase>;
  /** Sharer: stats del peer POR viewer (viewerId → stats), para la calidad y el aviso de estrés. */
  peerStats: Record<string, PeerStats>;
  role: Role | null;
  /** Esta pestaña es la que captura (sharer) o la que mira (viewer). */
  mine: boolean;
  /** Viewer: el video se movió a otra pestaña/ventana propia; acá solo queda "Bring back". */
  elsewhere: boolean;
  /** Viewer: esta pestaña TUVO el video alguna vez (distingue "se fue a una ventana" de "contestaron en otra pestaña"). */
  everMine: boolean;
  peerPhase: PeerPhase;
  stats: PeerStats | null;
  remoteStream: MediaStream | null;
  notice: Notice | null;
  pickerOpen: boolean;
  /** El selector abre para EMPEZAR un broadcast o para AGREGAR gente a uno en curso. */
  pickerMode: 'start' | 'add';
  macPerm: boolean;
  capture: { mic: Capture['mic']; hasSystemAudio: boolean; systemAudio: SystemAudioState; muted: boolean } | null;
  hint: 'detail' | 'motion';
  /** Viewer: el navegador no dejó reproducir CON sonido (NotAllowedError) → botón "Tap to hear audio". */
  audioBlocked: boolean;
  viewerMuted: boolean;
  /** Viewer: volumen local 0-1 (no viaja a nadie). */
  volume: number;
  /**
   * Viewer: SU micrófono (audio de ida y vuelta, spec §4.10). 'off' = no se pidió todavía o el gesto
   * no alcanzó (Safari) → botón "Turn on my microphone"; 'on' = viaja al sharer; 'denied' = permiso
   * negado; 'none' = sin dispositivo.
   */
  viewerMic: 'off' | 'on' | 'denied' | 'none';
  /** Viewer: silenció su propio mic (`track.enabled=false`, nunca se para); el sharer lo ve por `viewerMicMuted`. */
  viewerMicMuted: boolean;
  /** Sharer: el navegador no dejó reproducir la voz del viewer sin gesto (NotAllowedError) → "Tap to hear {name}". */
  hearBlocked: boolean;
  /**
   * Sharer: el AudioContext de la mezcla quedó suspendido, así que el track de audio SALIENTE es
   * silencio digital — el que mira no oye nada y NO hay ningún error (bug de prod 2026-09-08). La red
   * de seguridad lo reanuda en el primer gesto; el chip/banda lo hace visible mientras tanto.
   */
  mixerSuspended: boolean;
  sessionCutAt: string | null;
  idleLimitMs: number;
  connectedAt: number | null;
  poppedOut: 'doc_pip' | 'video_pip' | null;
  /** Viewer: el visor está minimizado (miniatura abajo a la derecha). Vive acá y no en el stage
   *  para que el botón de la barra ("Viewing {name}") pueda restaurarlo. */
  minimized: boolean;
};

const initialView: View = {
  socket: 'idle', socketReconnecting: false, pending: [], active: null, broadcast: null, peerPhases: {}, peerStats: {}, role: null, mine: false, elsewhere: false, everMine: false,
  peerPhase: 'new', stats: null, remoteStream: null, notice: null, pickerOpen: false, pickerMode: 'start', macPerm: false, capture: null,
  hint: 'detail', audioBlocked: false, viewerMuted: false, volume: 1, viewerMic: 'off', viewerMicMuted: false, hearBlocked: false, mixerSuspended: false,
  sessionCutAt: null, idleLimitMs: 0, connectedAt: null, poppedOut: null,
  minimized: false,
};

export type ShareContext = View & {
  /** Socket + tarjeta + visor (canShare || canView). */
  enabled: boolean;
  /** Puede INVITAR: office+ con `share_screen_enabled`. */
  canShare: boolean;
  /** Puede RECIBIR y ver: office+ habilitado o inspector con `share_view_enabled`. */
  canView: boolean;
  /** Es la app de campo (inspector): visor borde a borde, sin Pop out ni ventana propia. */
  fieldMode: boolean;
  windowMode: boolean;
  myId: string;
  videoEl: HTMLVideoElement | null;
  /** Abre el selector para empezar ('start', default) o para agregar a un broadcast en curso ('add'). */
  openPicker(mode?: 'start' | 'add'): void;
  closePicker(): void;
  startShare(target: TargetRow, also?: TargetRow[]): void;
  /** Agrega uno o varios viewers a MI broadcast en curso (sin cortar a los demás; no re-pide captura). */
  addViewers(targets: TargetRow[]): void;
  /** Quita a un viewer del broadcast (o cancela su invitación pendiente). */
  removeViewer(viewerId: string): void;
  cancelInvite(): void;
  stopSharing(): void;
  toggleMute(): void;
  accept(id: number): void;
  decline(id: number): void;
  leave(): void;
  bringBack(): void;
  openInWindow(): void;
  popOut(): Promise<void>;
  popOutVideo(): Promise<void>;
  setHint(h: 'detail' | 'motion'): void;
  setMinimized(m: boolean): void;
  setViewerMuted(m: boolean): void;
  setVolume(v: number): void;
  unmute(): void;
  /** Viewer: silenciar/activar SU micrófono (ida y vuelta). */
  toggleViewerMic(): void;
  /** Viewer: pedir el micrófono desde un clic (cuando el gesto de Accept no alcanzó o se negó por falta de gesto). */
  turnOnMic(): void;
  /** Sharer: "Tap to hear {name}" — el clic es el gesto que el autoplay del <audio> exigía. */
  hearViewer(): void;
  /** Sharer: reanuda el audio SALIENTE (AudioContext de la mezcla suspendido) desde un clic. */
  resumeAudio(): void;
  dismissNotice(): void;
  refreshState(): void;
  cooldownUntil(userId: string): number;
};

const Ctx = createContext<ShareContext | null>(null);

const STATE_EVERY_MS = 30_000;
const ALIVE_EVERY_MS = 60_000;
/** Un `/alive` perdido (parpadeo de red, 502 aislado) no puede tumbar una sesión cuyo video sigue:
 *  el barrido cierra a los 90 s, así que se reintenta a los 5 y 15 s antes de darse por vencido. */
const ALIVE_RETRY_MS = [5_000, 15_000];
const ICE_MAX_AGE_MS = 20 * 3_600_000;
const LAST_FRAME_MS = 2_000;
const DECLINE_COOLDOWN_MS = 30_000;
const POPUP_HANDOFF_WAIT_MS = 5_000;
/** Mientras el video vive en la ventana propia, la pestaña le pregunta cada 3 s si sigue ahí; a la
 *  segunda falta seguida lo trae de vuelta (la X de la ventana no siempre deja correr `pagehide`). */
const WINDOW_PING_MS = 3_000;
const WINDOW_PING_MISSES = 2;
const CHANNEL = 'ppag-share';

function isRetryable(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 0 || e.status === 502 || e.status === 503 || e.status === 504);
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Notificación del sistema con `requireInteraction` (Safari la ignora); best-effort. Devuelve el
 * objeto para que quien la disparó la CIERRE al aceptar/rechazar/vencer: con `requireInteraction`
 * se queda pegada en el escritorio hasta que alguien la cierre, incluso terminado el share.
 */
function systemNotification(title: string, body: string, tag: string): Notification | null {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return null;
    const n = new Notification(title, { body, tag, requireInteraction: true, icon: '/favicon.png' });
    n.onclick = () => { try { window.focus(); } catch { /* */ } n.close(); };
    return n;
  } catch { return null; }
}


export function ShareProvider({ children, windowSessionId }: { children?: ReactNode; windowSessionId?: number }) {
  const { me } = useAuth();
  const { t } = useI18n();
  const loc = useLocation();
  const windowMode = windowSessionId != null;
  const user = me?.kind === 'tenant' ? me.user : null;
  const myId = user?.id ?? '';
  const fieldMode = user?.role === 'inspector';
  const canShare = !!user && user.share_screen_enabled === true && user.role !== 'inspector';
  const canView = !!user && (user.share_screen_enabled === true || user.share_view_enabled === true);
  // El provider global se apaga en /share/view/:id: esa ventana monta el suyo (con `windowSessionId`)
  // y dos providers sobre el mismo TAB_ID armarían dos peers para la misma sesión.
  const inShareWindowRoute = !windowMode && /^\/share\/view\//.test(loc.pathname);
  const enabled = canView && !inShareWindowRoute;

  const [view, setView] = useState<View>(initialView);
  const viewRef = useRef(view);
  viewRef.current = view;
  const patch = useCallback((p: Partial<View> | ((v: View) => Partial<View>)) => {
    setView((v) => ({ ...v, ...(typeof p === 'function' ? p(v) : p) }));
  }, []);

  // Cosas que NO son estado de render: viven en refs y sobreviven re-renders.
  // Viewer: UN peer al sharer (camino idéntico a antes). Sharer: UN peer POR viewer (malla, spec §14).
  const peerRef = useRef<SharePeer | null>(null);
  const peersRef = useRef<Map<string, SharePeer>>(new Map());
  /** Sharer: viewerId → pestaña a la que apunta su peer (para detectar un handoff y re-apuntar). */
  const peerMetaRef = useRef<Map<string, string | null>>(new Map());
  const captureRef = useRef<Capture | null>(null);
  const cursorRef = useRef<SignalCursor>(newCursor());
  const iceRef = useRef<{ servers: RTCIceServer[]; mintedAt: number; maxBitrateKbps: number | null }>({ servers: [], mintedAt: 0, maxBitrateKbps: null });
  const seenRef = useRef<Set<number>>(new Set());
  const cooldownRef = useRef<Map<string, number>>(new Map());
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const docPipRef = useRef<DocPipHandle | null>(null);
  const titleRef = useRef<{ original: string; timer: number | null }>({ original: '', timer: null });
  const chimeRef = useRef<number | null>(null);
  const endTimerRef = useRef<number | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const notifRef = useRef<Notification | null>(null);
  /** Ventana propia que se cierra a propósito (Leave / "Bring back" desde la pestaña): su
   *  `pagehide` NO debe pedirle a la pestaña principal que traiga el video de vuelta. */
  const quietCloseRef = useRef(false);
  const bootedRef = useRef(false);
  const popupWaitRef = useRef<number | null>(null);
  /** Sesión cuyo accept/handoff está en vuelo: el share.update puede llegar ANTES que la respuesta
   *  con los iceServers, y el peer se construye recién con ellos (si no, sale sin TURN). */
  const joiningRef = useRef<number | null>(null);
  /** Sesión para la que ya se reportó `audioBlocked` (un POST por sesión, no uno por `ontrack`). */
  const audioBlockedReportedRef = useRef<number | null>(null);
  /** Viewer: el stream del micrófono propio (ida y vuelta). Vive acá, no en el peer: sobrevive a un rebuild. */
  const micStreamRef = useRef<MediaStream | null>(null);
  /** Lo último que se reportó de `viewerMic` en `/connected` (un POST por cambio, no por reconexión). */
  const viewerMicReportedRef = useRef<{ sid: number; on: boolean } | null>(null);
  /** Viewer: el <audio> oculto donde suena la voz del viewer que llega al sharer (1:1) / el de calentamiento. */
  const audioRef = useRef<HTMLAudioElement | null>(null);
  /** Sharer: un <audio> oculto POR viewer (malla): el sharer oye a cada uno. viewerId → elemento. */
  const audioElsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  /** Sesión cuyo `report-connected` del sharer ya se envió, POR viewer (un POST por viewer, no por reconexión). */
  const connectReportedRef = useRef<Set<string>>(new Set());
  const tRef = useRef(t);
  tRef.current = t;
  // Callbacks que se usan desde closures creados ANTES de su declaración (buildPeer → replay; el
  // canal entre pestañas → bringBack): se leen por ref para no arrastrar dependencias circulares.
  const replaySignalsRef = useRef<(id: number) => Promise<void>>(async () => {});
  const bringBackRef = useRef<() => void>(() => {});

  // El <video> es NUESTRO, no de React: Document PiP lo mueve de ventana y la miniatura lo
  // re-parenta; con React manejándolo, cada re-render lo devolvería a su lugar.
  if (videoRef.current === null && typeof document !== 'undefined') {
    const v = document.createElement('video');
    v.autoplay = true;
    v.playsInline = true;
    v.muted = true;
    v.className = 'ss-video';
    videoRef.current = v;
  }
  /**
   * El <audio> del sharer (voz del viewer, ida y vuelta) también es nuestro. En la malla hay UNO POR
   * viewer (`key` = viewerId; `'__warm__'` es el de calentamiento del gesto). Se crea al PRIMER USO y
   * fuera del render: con el patrón del <video> (crear en el cuerpo del componente) StrictMode invoca
   * el render dos veces y dejaba DOS elementos en el body, uno huérfano (medido en el e2e). Va al
   * body porque sin controles no ocupa nada y algún WebKit no reproduce un elemento de media suelto.
   */
  const remoteAudioEl = useCallback((key = '__warm__'): HTMLAudioElement | null => {
    const existing = audioElsRef.current.get(key);
    if (existing) return existing;
    if (typeof document === 'undefined') return null;
    const a = document.createElement('audio');
    a.autoplay = true;
    a.className = 'ss-remote-audio';
    // Con la malla hay un <audio> por viewer (más el de calentamiento '__warm__'): la marca por viewer
    // permite distinguirlos (el e2e apunta al del viewer real, no al primer que devuelva querySelector).
    a.dataset.viewer = String(key);
    a.setAttribute('aria-hidden', 'true');
    try { document.body.appendChild(a); } catch { /* sin body todavía: igual reproduce suelto en Chromium */ }
    audioElsRef.current.set(key, a);
    if (key === '__warm__') audioRef.current = a;
    return a;
  }, []);

  /** Suelta y quita el <audio> de un viewer (su peer se cerró): sin esto quedaría sonando huérfano. */
  const dropAudioEl = useCallback((key: string) => {
    const a = audioElsRef.current.get(key);
    if (!a) return;
    try { a.srcObject = null; a.remove(); } catch { /* */ }
    audioElsRef.current.delete(key);
  }, []);

  // ───────────────────────── helpers de sesión ─────────────────────────

  const roleOf = useCallback((s: SessionDTO | null): Role | null => {
    if (!s) return null;
    if (s.sharer.id === myId) return 'sharer';
    if (s.viewer.id === myId) return 'viewer';
    return null;
  }, [myId]);

  const isMineTab = useCallback((s: SessionDTO, role: Role | null): boolean => {
    if (role === 'sharer') return s.sharerTabId === TAB_ID;
    if (role === 'viewer') return s.viewerTabId === TAB_ID;
    return false;
  }, []);

  const stopTitleBlink = useCallback(() => {
    const tr = titleRef.current;
    if (tr.timer != null) { clearInterval(tr.timer); tr.timer = null; document.title = tr.original; }
  }, []);

  const stopChime = useCallback(() => {
    if (chimeRef.current != null) { clearInterval(chimeRef.current); chimeRef.current = null; }
  }, []);

  /** Viewer: cierra SU peer al sharer (el sharer usa `teardownMesh`). */
  const teardownPeer = useCallback((clearVideo: boolean) => {
    const p = peerRef.current;
    peerRef.current = null;
    cursorRef.current = newCursor();
    p?.close();
    // Cerrar el peer congela el <video> en el último cuadro; se limpia solo cuando se pide.
    if (clearVideo && videoRef.current) setVideoSource(videoRef.current, null);
    patch({ remoteStream: null, stats: null, peerPhase: 'new', connectedAt: null, hearBlocked: false });
  }, [patch]);

  /** Sharer: cierra TODOS los peers de la malla y sus <audio> por viewer. Idempotente. */
  const teardownMesh = useCallback(() => {
    for (const peer of peersRef.current.values()) peer.close();
    peersRef.current.clear();
    peerMetaRef.current.clear();
    connectReportedRef.current.clear();
    for (const key of [...audioElsRef.current.keys()]) { if (key !== '__warm__') dropAudioEl(key); }
    if (audioRef.current) { try { audioRef.current.srcObject = null; } catch { /* */ } }
    cursorRef.current = newCursor();
    patch({ peerPhases: {}, peerStats: {}, connectedAt: null, hearBlocked: false });
  }, [dropAudioEl, patch]);

  const teardownCapture = useCallback(() => {
    captureRef.current?.stop();
    captureRef.current = null;
    patch({ capture: null, mixerSuspended: false });
  }, [patch]);

  /** Viewer: se para el micrófono propio (fin de sesión, o el video se fue a otra pestaña/ventana). */
  const teardownViewerMic = useCallback(() => {
    const ms = micStreamRef.current;
    micStreamRef.current = null;
    for (const t of ms?.getTracks() ?? []) { try { t.stop(); } catch { /* */ } }
    viewerMicReportedRef.current = null;
    patch({ viewerMic: 'off', viewerMicMuted: false });
  }, [patch]);

  /**
   * UNA sola fuente para el mute del viewer: la fila (`stats_json.viewer_mic_muted`, viaja como
   * `viewerMicMuted`). El estado local se resetea en cada teardown (ventana propia, recarga, Bring
   * back) pero el servidor lo conserva —el handoff no lo toca—, así que la pestaña/ventana que
   * recupera el micrófono lo arranca como estaba: si se había silenciado, sigue en silencio (el que
   * se muteó no queda audible por abrir una ventana) y el botón/chip de los dos lados coinciden.
   * Se llama con la respuesta de accept/handoff, ANTES de armar el peer.
   */
  const syncViewerMicMuted = useCallback((s: SessionDTO) => {
    const muted = Boolean(s.viewerMicMuted);
    for (const t of micStreamRef.current?.getAudioTracks() ?? []) t.enabled = !muted;
    patch({ viewerMicMuted: muted });
  }, [patch]);

  /**
   * Un POST por cambio real: `stats_json.viewerMic` dice si el que mira habló con micrófono (spec §7:
   * /health cuenta el %). Se manda al conectar y otra vez si el mic se prende después.
   */
  const reportViewerMic = useCallback((sid: number, on: boolean) => {
    const prev = viewerMicReportedRef.current;
    if (prev && prev.sid === sid && prev.on === on) return;
    viewerMicReportedRef.current = { sid, on };
    void api.post(`/api/share/${sid}/connected`, { audio: { viewerMic: on } }).catch(() => {});
  }, []);

  /**
   * Viewer: pedir SU micrófono (audio de ida y vuelta, spec §4.10). Se llama DENTRO del gesto cuando
   * lo hay (Accept, "Bring back", "Turn on my microphone") porque Safari exige activación para
   * `getUserMedia`; Chromium pide permiso igual sin gesto (ventana propia, recarga). Negado o sin
   * dispositivo → se sigue mirando como antes (solo escucha) y el visor dice "No microphone".
   * El track se le da al peer cuando exista: si ya contestó la oferta entra por `replaceTrack`.
   */
  const requestViewerMic = useCallback((): Promise<MediaStreamTrack | null> => {
    const have = micStreamRef.current?.getAudioTracks()[0];
    if (have && have.readyState === 'live') return Promise.resolve(have);
    if (!navigator.mediaDevices?.getUserMedia) { patch({ viewerMic: 'none' }); return Promise.resolve(null); }
    let p: Promise<MediaStream>;
    try { p = navigator.mediaDevices.getUserMedia({ audio: micConstraints() }); }
    catch (e) { p = Promise.reject(e); }
    return p.then((ms) => {
      const v = viewRef.current;
      // Mientras viajaba el permiso la sesión se fue (accept fallido, Leave): no queda un mic abierto.
      if (v.role !== 'viewer' && joiningRef.current == null) { for (const t of ms.getTracks()) t.stop(); return null; }
      if (micStreamRef.current) { for (const t of ms.getTracks()) t.stop(); return micStreamRef.current.getAudioTracks()[0] ?? null; }
      const track = ms.getAudioTracks()[0] ?? null;
      if (!track) { patch({ viewerMic: 'none' }); return null; }
      micStreamRef.current = ms;
      // El mic puede llegar ANTES que la respuesta del handoff (active aún null en el boot): se toma
      // lo persistido si ya está; si no, `syncViewerMicMuted` lo cuadra cuando llegue la sesión.
      const muted = Boolean(viewRef.current.active?.viewerMicMuted) || v.viewerMicMuted;
      track.enabled = !muted;
      patch({ viewerMic: 'on', viewerMicMuted: muted });
      peerRef.current?.setMicTrack(track);
      const cur = viewRef.current.active;
      // Ya estaba conectado (mic prendido después): se corrige lo reportado. Si no, lo reporta el 'connected'.
      if (cur && viewerMicReportedRef.current?.sid === cur.id) reportViewerMic(cur.id, true);
      track.addEventListener('ended', () => {
        // Se desenchufó el auricular / el sistema quitó el mic: vuelve el botón para pedirlo de nuevo.
        if (micStreamRef.current !== ms) return;
        micStreamRef.current = null;
        peerRef.current?.setMicTrack(null);
        patch({ viewerMic: 'off' });
      });
      return track;
    }).catch((e: unknown) => {
      // NotFoundError = no hay dispositivo; lo demás es un permiso negado (o sin gesto en Safari: el
      // botón "Turn on my microphone" lo vuelve a intentar desde un clic).
      patch({ viewerMic: (e as { name?: string })?.name === 'NotFoundError' ? 'none' : 'denied' });
      return null;
    });
  }, [patch, reportViewerMic]);

  /** Fin de MI sesión (cualquier motivo): limpia peer, captura, PiP y deja el aviso que toque. */
  const finishSession = useCallback((s: SessionDTO | null, notice: Notice | null, opts: { lastFrame?: boolean } = {}) => {
    if (docPipRef.current) { docPipRef.current.close(); docPipRef.current = null; }
    void exitVideoPip();
    if (endTimerRef.current != null) { clearTimeout(endTimerRef.current); endTimerRef.current = null; }
    joiningRef.current = null;
    if (popupWaitRef.current != null) { clearTimeout(popupWaitRef.current); popupWaitRef.current = null; }
    const v0 = viewRef.current;
    const wasWatching = opts.lastFrame === true && v0.role === 'viewer' && v0.mine && !!v0.remoteStream;
    teardownPeer(!wasWatching);
    teardownCapture();
    teardownViewerMic();
    stopTitleBlink();
    stopChime();
    const clear = () => patch((v) => ({ active: null, role: null, mine: false, elsewhere: false, everMine: false, poppedOut: null, audioBlocked: false, hearBlocked: false, mixerSuspended: false, minimized: false, notice: notice ?? v.notice, pending: s ? v.pending.filter((p) => p.id !== s.id) : v.pending }));
    if (wasWatching) {
      // El otro terminó: el último cuadro se queda 2 s con el aviso encima, después se cierra.
      patch({ notice, peerPhase: 'closed' });
      endTimerRef.current = window.setTimeout(() => {
        endTimerRef.current = null;
        if (videoRef.current) setVideoSource(videoRef.current, null);
        clear();
      }, LAST_FRAME_MS);
    } else {
      clear();
    }
  }, [patch, stopChime, stopTitleBlink, teardownCapture, teardownPeer, teardownViewerMic]);

  /** Fin de MI broadcast (sharer): cierra la malla, la captura y deja el aviso que toque. */
  const finishBroadcast = useCallback((notice: Notice | null) => {
    joiningRef.current = null;
    teardownMesh();
    teardownCapture();
    stopTitleBlink();
    stopChime();
    patch((v) => ({ broadcast: null, role: null, mine: false, peerPhases: {}, peerStats: {}, mixerSuspended: false, notice: notice ?? v.notice }));
  }, [patch, stopChime, stopTitleBlink, teardownCapture, teardownMesh]);

  /** Aviso del sharer cuando su broadcast entero termina (el propio Stop no deja aviso). */
  const noticeForBroadcastEnd = useCallback((b: BroadcastDTO): Notice | null => {
    if (b.status === 'failed' || b.endReason === 'ice_failed' || b.endReason === 'timeout') return { kind: 'lost' };
    if (b.endReason === 'session_cut') return null;
    return null;
  }, []);

  const noticeForEnd = useCallback((s: SessionDTO, role: Role | null): Notice | null => {
    const other = role === 'sharer' ? s.viewer.name : s.sharer.name;
    switch (s.status) {
      case 'declined': return role === 'sharer' ? { kind: 'declined', name: other } : null;
      case 'expired': return role === 'sharer' ? { kind: 'noAnswer', name: other } : null;
      case 'cancelled': return null;
      case 'failed': return { kind: 'lost', name: other };
      case 'ended':
        if (s.endReason === 'viewer_left') return role === 'sharer' ? { kind: 'viewerLeft', name: other } : null;
        if (s.endReason === 'sharer_stopped' || s.endReason === 'sharer_left') return role === 'viewer' ? { kind: 'endedBy', name: other } : null;
        if (s.endReason === 'timeout' || s.endReason === 'ice_failed') return { kind: 'lost', name: other };
        if (s.endReason === 'session_cut') return null;
        return { kind: 'endedBy', name: other };
      default: return null;
    }
  }, []);

  // ───────────────────────── REST ─────────────────────────

  // Sharer: `toUserId` OBLIGATORIO (a qué viewer de la malla va la señal); viewer: se omite (el
  // servidor la enruta al sharer). `from_tab_id` lo llena el servidor con MI pestaña.
  const sendSignal = useCallback(async (id: number, kind: SignalKind, payload: Record<string, unknown>, toTabId: string | null, toUserId?: string) => {
    await api.post(`/api/share/${id}/signal`, { kind, payload, toTabId: toTabId ?? undefined, toUserId: toUserId ?? undefined });
  }, []);

  const refreshIceIfOld = useCallback(async (id: number) => {
    if (Date.now() - iceRef.current.mintedAt < ICE_MAX_AGE_MS) return;
    try {
      const r = await api.get<{ iceServers: RTCIceServer[] }>(`/api/share/${id}/ice`);
      iceRef.current = { ...iceRef.current, servers: r.iceServers, mintedAt: Date.now() };
      peerRef.current?.setIceServers(r.iceServers);
    } catch { /* se sigue con las que hay */ }
  }, []);

  /**
   * Viewer: reproducir el stream que llegó. Primero CON sonido; si el navegador exige gesto
   * (NotAllowedError: Safari, o una pestaña sin clic previo) se reproduce mudo y se ofrece "Tap to
   * hear audio". `ontrack` dispara una vez por track (video y audio) con el MISMO stream: la
   * segunda vez no se reasigna el srcObject (un `play()` interrumpido da AbortError, que no es un
   * bloqueo de audio).
   */
  const playRemote = useCallback((stream: MediaStream) => {
    const v = videoRef.current;
    if (!v) return;
    if (v.srcObject !== stream) setVideoSource(v, stream);
    const cur = viewRef.current;
    v.volume = cur.volume;
    v.muted = cur.viewerMuted;
    v.play().then(() => {
      if (!cur.viewerMuted) patch({ audioBlocked: false });
    }).catch((e: unknown) => {
      if ((e as { name?: string })?.name !== 'NotAllowedError') return;
      // Sin gesto válido para audio: mudo y con el botón grande a la vista.
      v.muted = true;
      patch({ audioBlocked: true });
      v.play().catch(() => {});
      // Se anota UNA vez por sesión en la fila (`stats_json.audioBlocked` → `audio_blocked` por UA en
      // System health, spec §7): sin esto el contador leería una columna que nadie escribe.
      const sid = cur.active?.id;
      if (sid != null && audioBlockedReportedRef.current !== sid) {
        audioBlockedReportedRef.current = sid;
        void api.post(`/api/share/${sid}/connected`, { audioBlocked: true }).catch(() => {});
      }
    });
  }, [patch]);

  /**
   * Sharer: reproducir la voz del viewer (ida y vuelta). El clic de compartir fue el gesto, así que
   * normalmente suena solo; si el navegador igual exige uno (NotAllowedError) queda "Tap to hear {name}"
   * en la píldora. NO se mezcla en el mixer de la captura: lo que se manda al viewer es la pantalla y
   * el mic del sharer, nunca la propia voz del viewer de vuelta. OJO Windows con "pantalla completa":
   * el audio del sistema capturado puede recoger esta voz remota (sale por los parlantes) — la pista
   * `share.headphones` existe por eso; `restrictOwnAudio` solo ayuda al capturar una pestaña.
   */
  const playRemoteAudio = useCallback((viewerId: string, stream: MediaStream) => {
    const a = remoteAudioEl(viewerId);
    if (!a) return;
    if (a.srcObject !== stream) a.srcObject = stream;
    a.muted = false;
    a.play().then(() => patch({ hearBlocked: false })).catch((e: unknown) => {
      if ((e as { name?: string })?.name !== 'NotAllowedError') return;
      patch({ hearBlocked: true });
    });
  }, [patch, remoteAudioEl]);

  /** Sharer: reintenta reproducir la voz de TODOS los viewers (tras un gesto que destrabó el autoplay). */
  const playAllRemoteAudio = useCallback(() => {
    let anyBlocked = false;
    for (const [key, a] of audioElsRef.current) {
      if (key === '__warm__' || !a.srcObject) continue;
      a.muted = false;
      a.play().catch((e: unknown) => { if ((e as { name?: string })?.name === 'NotAllowedError') anyBlocked = true; });
    }
    if (!anyBlocked) patch({ hearBlocked: false });
  }, [patch]);

  /**
   * VIEWER: crea SU único peer al sharer (camino idéntico a antes de la malla). El sharer ahora usa
   * `buildSharerPeer`/`reconcilePeers`.
   */
  const buildPeer = useCallback((s: SessionDTO, role: Role = 'viewer') => {
    if (role === 'sharer') return; // el sharer nunca pasa por acá (malla): lo maneja reconcilePeers
    peerRef.current?.close();
    cursorRef.current = newCursor();
    const sid = s.id;
    const peer = new SharePeer({
      role: 'viewer',
      iceServers: iceRef.current.servers,
      maxBitrateKbps: iceRef.current.maxBitrateKbps ?? s.maxBitrateKbps,
      peerTabId: s.sharerTabId,
      send: (kind, payload, to) => sendSignal(sid, kind, payload, to),
      onPhase: (st: PeerState, action: PeerAction) => {
        patch({ peerPhase: st.phase });
        if (action === 'report-connected') {
          patch({ connectedAt: Date.now() });
          // Viewer: ¿habló con micrófono? (ida y vuelta, spec §7: /health cuenta el %).
          reportViewerMic(sid, !!micStreamRef.current);
        } else if (action === 'restart-ice' || action === 'force-relay') {
          void refreshIceIfOld(sid);
        } else if (action === 'give-up') {
          const cur = viewRef.current.active;
          if (cur) finishSession(cur, { kind: 'lost', name: cur.sharer.name });
        }
      },
      onStats: (stats) => { patch({ stats }); },
      // Viewer: 20 s tras su answer sin conectar; se repone lo que el socket pudo perder.
      onStall: () => { void replaySignalsRef.current(sid); },
      onRemoteStream: (stream) => { patch({ remoteStream: stream }); playRemote(stream); },
      micTrack: micStreamRef.current?.getAudioTracks()[0] ?? null,
    });
    peerRef.current = peer;
    peer.start();
  }, [finishSession, patch, playRemote, refreshIceIfOld, reportViewerMic, sendSignal]);

  /**
   * SHARER: crea UN peer hacia un viewer de la malla (reusa `SharePeer` tal cual). La captura es la
   * MISMA para todos; el <audio> de la voz de vuelta es POR viewer. NO resetea el cursor global (los
   * otros peers comparten el stream de señales demultiplexado por `from_user_id`).
   */
  const buildSharerPeer = useCallback((broadcastId: number, p: ParticipantDTO, bitrateKbps: number) => {
    const viewerId = p.viewer.id;
    peersRef.current.get(viewerId)?.close();
    connectReportedRef.current.delete(viewerId);
    const peer = new SharePeer({
      role: 'sharer',
      iceServers: iceRef.current.servers,
      maxBitrateKbps: bitrateKbps,
      peerTabId: p.viewerTabId,
      send: (kind, payload, to) => sendSignal(broadcastId, kind, payload, to, viewerId),
      onPhase: (st: PeerState, action: PeerAction) => {
        patch((v) => ({ peerPhases: { ...v.peerPhases, [viewerId]: st.phase } }));
        if (action === 'report-connected') {
          patch({ connectedAt: Date.now() });
          if (connectReportedRef.current.has(viewerId)) return;
          connectReportedRef.current.add(viewerId);
          void (async () => {
            const stats = await peer.sampleStats();
            const cap = captureRef.current;
            await api.post(`/api/share/${broadcastId}/connected`, {
              viewerUserId: viewerId,
              candidatePair: stats?.pair ?? 'host',
              relayUsed: stats?.relayUsed ?? false,
              setupMs: Date.now() - peer.startedAt,
              audio: { mic: cap?.mic === 'on', system: cap?.hasSystemAudio === true },
            }).catch(() => {});
          })();
        } else if (action === 'restart-ice' || action === 'force-relay') {
          void refreshIceIfOld(broadcastId);
        } else if (action === 'give-up') {
          // Un solo viewer perdió ICE: se lo saca de la malla, el broadcast sigue para los demás.
          peersRef.current.get(viewerId)?.close();
          peersRef.current.delete(viewerId);
          peerMetaRef.current.delete(viewerId);
          dropAudioEl(viewerId);
          patch((v) => {
            const phases = { ...v.peerPhases }; delete phases[viewerId];
            const st = { ...v.peerStats }; delete st[viewerId];
            return { peerPhases: phases, peerStats: st };
          });
          // ¿Quedan OTROS viewers vivos? Si este era el ÚLTIMO, el broadcast se queda sin nadie: se
          // termina como 'ice_failed' (→ aviso "Connection lost", como el 1:1 de antes), no con un
          // /remove que lo dejaría caer en el auto-fin de broadcast vacío SIN aviso (el filtro 'gone'
          // de applyBroadcast no contempla 'removed'). Con más viewers, solo se lo saca de la malla.
          const bc = viewRef.current.broadcast;
          const otrosVivos = (bc?.participants ?? []).some((q) => q.viewer.id !== viewerId
            && (q.status === 'pending' || q.status === 'accepted' || q.status === 'live'));
          if (bc && !otrosVivos) {
            void api.post(`/api/share/${broadcastId}/end`, { reason: 'ice_failed' }).catch(() => {});
            finishBroadcast({ kind: 'lost', name: p.viewer.name });
          } else {
            void api.post(`/api/share/${broadcastId}/participants/${p.id}/remove`, {}).catch(() => {});
          }
        }
      },
      onStats: (stats) => {
        patch((v) => ({ peerStats: { ...v.peerStats, [viewerId]: stats } }));
        const cap = captureRef.current;
        if (cap) {
          const susp = cap.isAudioSuspended();
          if (susp !== viewRef.current.mixerSuspended) patch({ mixerSuspended: susp });
        }
      },
      onRemoteStream: () => { /* el sharer no recibe video, solo la voz por onRemoteAudio */ },
      onRemoteAudio: (stream) => playRemoteAudio(viewerId, stream),
      micTrack: null,
    });
    if (captureRef.current) peer.attach(captureRef.current.stream);
    peersRef.current.set(viewerId, peer);
    peerMetaRef.current.set(viewerId, p.viewerTabId);
    peer.start();
  }, [dropAudioEl, finishBroadcast, patch, playRemoteAudio, refreshIceIfOld, sendSignal]);

  /**
   * SHARER: reconcilia la malla contra el roster del broadcast (spec §14): arma un peer por viewer
   * nuevo, re-apunta al que se movió de pestaña, cierra al que salió/echaron, y aplica el cap de
   * bitrate por viewer (que BAJA al crecer el número: presupuesto de subida repartido).
   */
  const reconcilePeers = useCallback((b: BroadcastDTO) => {
    if (!captureRef.current) return;
    const existing = [...peersRef.current.keys()].map((viewerId) => ({ viewerId, peerTabId: peerMetaRef.current.get(viewerId) ?? null }));
    const plan = planReconcile(b, existing);
    for (const viewerId of plan.close) {
      peersRef.current.get(viewerId)?.close();
      peersRef.current.delete(viewerId);
      peerMetaRef.current.delete(viewerId);
      connectReportedRef.current.delete(viewerId);
      dropAudioEl(viewerId);
      patch((v) => {
        const phases = { ...v.peerPhases }; delete phases[viewerId];
        const st = { ...v.peerStats }; delete st[viewerId];
        return { peerPhases: phases, peerStats: st };
      });
    }
    for (const p of plan.retarget) {
      const peer = peersRef.current.get(p.viewer.id);
      if (peer) { peer.retarget(p.viewerTabId); peerMetaRef.current.set(p.viewer.id, p.viewerTabId); }
    }
    for (const p of plan.add) buildSharerPeer(b.id, p, plan.bitrateKbps);
    // El reparto de subida cambió: se aplica el nuevo cap a CADA peer de la malla.
    for (const peer of peersRef.current.values()) peer.setMaxBitrate(plan.bitrateKbps);
  }, [buildSharerPeer, dropAudioEl, patch]);
  const reconcileRef = useRef(reconcilePeers);
  reconcileRef.current = reconcilePeers;

  const applySignal = useCallback((sig: SignalDTO) => {
    const v = viewRef.current;
    // Ancla: el sharer la lleva en `broadcast`; el viewer en `active`. La señal es de MI sesión.
    const anchorId = v.role === 'sharer' ? v.broadcast?.id : v.active?.id;
    if (anchorId == null || sig.sessionId !== anchorId) return;
    if (sig.toTabId && sig.toTabId !== TAB_ID) return;
    const r = advanceCursor(cursorRef.current, sig.id);
    if (!r.apply) return;
    cursorRef.current = r.cursor;
    // Demux (malla): el sharer recibe UN stream mezclado de todos sus viewers y enruta por `fromUserId`
    // al peer de ese viewer; el viewer tiene un solo peer.
    if (v.role === 'sharer') void peersRef.current.get(sig.fromUserId)?.handleSignal(sig.kind, sig.payload);
    else void peerRef.current?.handleSignal(sig.kind, sig.payload);
  }, []);

  const replaySignals = useCallback(async (id: number) => {
    try {
      const r = await api.get<{ rows: SignalDTO[] }>(`/api/share/${id}/signals?after=${cursorRef.current.lastId}`);
      for (const sig of r.rows) applySignal(sig);
    } catch { /* la próxima señal o el próximo resync lo reponen */ }
  }, [applySignal]);
  replaySignalsRef.current = replaySignals;

  // ¿Alguna otra pestaña MÍA tiene viva esta sesión? (para no robarle el video al recargar)
  const askSiblings = useCallback((sessionId: number): Promise<boolean> => {
    const ch = channelRef.current;
    if (!ch) return Promise.resolve(false);
    return new Promise((resolve) => {
      const nonce = Math.random().toString(36).slice(2);
      const onMsg = (e: MessageEvent) => {
        const d = e.data as { t?: string; nonce?: string };
        if (d?.t === 'share.alive' && d.nonce === nonce) { ch.removeEventListener('message', onMsg); clearTimeout(to); resolve(true); }
      };
      const to = setTimeout(() => { ch.removeEventListener('message', onMsg); resolve(false); }, 350);
      ch.addEventListener('message', onMsg);
      ch.postMessage({ t: 'share.who', sessionId, nonce });
    });
  }, []);

  /**
   * Aplica al VIEWER una sesión que llegó por socket o por /state. El SHARER ya no recibe SessionDTO:
   * su estado llega como `BroadcastDTO` por `applyBroadcast`.
   */
  const applySession = useCallback(async (s: SessionDTO, source: 'socket' | 'state' | 'boot') => {
    const role = roleOf(s);
    if (role !== 'viewer') return; // en la malla, todo SessionDTO que me llega es como viewer
    const cur = viewRef.current.active;
    const terminal = !LIVE_STATUSES.has(s.status);

    // Invitación PARA MÍ (viewer, pending): tarjeta.
    if (s.status === 'pending') {
      patch((v) => ({ pending: v.pending.some((p) => p.id === s.id) ? v.pending.map((p) => (p.id === s.id ? s : p)) : [...v.pending, s] }));
      return;
    }

    if (terminal) {
      if (cur && cur.id === s.id) {
        finishSession(s, noticeForEnd(s, role), { lastFrame: true });
      } else {
        // Una invitación que venció/cancelaron antes de contestar; o contestada en otra pestaña.
        patch((v) => ({ pending: v.pending.filter((p) => p.id !== s.id) }));
        if ((s.status === 'declined' || s.status === 'accepted') && s.viewerTabId && s.viewerTabId !== TAB_ID) {
          patch({ notice: { kind: 'answeredElsewhere' } });
        }
      }
      if (viewRef.current.pending.length === 0 && !viewRef.current.active) { stopTitleBlink(); stopChime(); }
      return;
    }

    // accepted / live (viewer)
    const mine = isMineTab(s, role);
    patch((v) => ({ active: s, role, mine, elsewhere: !mine, everMine: v.everMine || mine, pending: v.pending.filter((p) => p.id !== s.id) }));
    stopTitleBlink(); stopChime();

    // viewer
    if (!mine) {
      // Mi accept/handoff está en vuelo: la sesión que llega por el socket todavía trae el
      // `viewerTabId` VIEJO. La respuesta del POST es la que fija el estado; sin este corte la
      // ventana propia se cerraba sola ("moved back") si el sharer pasaba a live o muteaba en el
      // hueco entre su /handoff y la respuesta (TURN mint hasta 1,5 s).
      if (joiningRef.current === s.id) return;
      // El video vive en otra pestaña/ventana. Si esta es una recarga (nadie más la tiene), se
      // recupera sola con un handoff; si hay otra pestaña viva, se deja "Bring back" a mano.
      if (peerRef.current) { teardownPeer(false); }
      // El micrófono va con el video: la pestaña/ventana que lo tiene pide el suyo.
      if (micStreamRef.current) teardownViewerMic();
      if (docPipRef.current) { docPipRef.current.close(); docPipRef.current = null; }
      if (popupWaitRef.current != null) { clearTimeout(popupWaitRef.current); popupWaitRef.current = null; }
      if (windowMode) {
        // La ventana propia perdió el video (lo trajeron de vuelta): se cierra sola.
        quietCloseRef.current = true;
        patch({ notice: { kind: 'movedBack' } });
        setTimeout(() => { try { window.close(); } catch { /* */ } }, 800);
        return;
      }
      if (source === 'boot' && !(await askSiblings(s.id))) {
        joiningRef.current = s.id;
        // Recarga sin gesto: Chromium da el mic igual (permiso ya concedido); si no, queda el botón.
        void requestViewerMic();
        try {
          const r = await api.post<HandoffResponse>(`/api/share/${s.id}/handoff`, { tabId: TAB_ID, ua: navigator.userAgent });
          iceRef.current = { servers: r.iceServers, mintedAt: Date.now(), maxBitrateKbps: r.session.maxBitrateKbps };
          joiningRef.current = null;
          patch({ active: r.session, mine: true, elsewhere: false, everMine: true });
          syncViewerMicMuted(r.session);
          buildPeer(r.session);
          await replaySignals(r.session.id);
        } catch { joiningRef.current = null; /* queda el "Bring back" */ }
      }
      return;
    }
    // Mientras el accept/handoff está en vuelo, el peer lo construye esa respuesta (con TURN).
    if (joiningRef.current === s.id) return;
    if (!peerRef.current) buildPeer(s);
    if (source !== 'socket') void replaySignals(s.id);
  }, [askSiblings, buildPeer, finishSession, isMineTab, noticeForEnd, patch, replaySignals, requestViewerMic, roleOf, stopChime, stopTitleBlink, syncViewerMicMuted, teardownPeer, teardownViewerMic, windowMode]);

  /**
   * SHARER: aplica MI broadcast (llegó por `share.broadcast`, `/state` o la respuesta de `/invites`).
   * Reconcilia la malla contra el roster; si otra pestaña mía captura, no arma peers acá.
   */
  const applyBroadcast = useCallback(async (b: BroadcastDTO, source: 'socket' | 'state' | 'boot') => {
    if (b.sharer.id !== myId) return;
    const terminal = b.status === 'ended' || b.status === 'cancelled' || b.status === 'failed';
    const prev = viewRef.current.broadcast;
    if (terminal) {
      if (prev && prev.id === b.id) finishBroadcast(noticeForBroadcastEnd(b));
      return;
    }
    const mine = b.sharerTabId === TAB_ID;
    // El broadcast se quedó SIN espectadores (el/los último/s declinaron, se fueron o no contestaron):
    // en la malla no tiene sentido seguir capturando la pantalla para nadie, así que se termina y se
    // avisa al sharer por-viewer (el toast se muestra recién sin `broadcast`, ver ShareNoticeToast).
    // Esto conserva el 1:1 de siempre: el otro se va → aviso + la píldora se cierra. Para "seguir vivo
    // con 0 y Add people" habría que abrir el selector en vez de terminar; este es el punto a cambiar.
    const activos = (ps: ParticipantDTO[]) => ps.filter((p) => p.status === 'pending' || p.status === 'accepted' || p.status === 'live');
    if (mine && prev && prev.id === b.id && activos(prev.participants).length > 0 && activos(b.participants).length === 0) {
      const gone = b.participants
        .filter((p) => p.status === 'declined' || p.status === 'expired' || p.status === 'left' || p.status === 'failed')
        .sort((x, y) => Date.parse(y.endedAt ?? '') - Date.parse(x.endedAt ?? ''))[0];
      const notice: Notice | null = gone?.status === 'declined' ? { kind: 'declined', name: gone.viewer.name }
        : gone?.status === 'expired' ? { kind: 'noAnswer', name: gone.viewer.name }
          : gone?.status === 'failed' ? { kind: 'lost', name: gone.viewer.name }
            : gone?.status === 'left' ? { kind: 'viewerLeft', name: gone.viewer.name }
              : null;
      void api.post(`/api/share/${b.id}/end`, { reason: gone?.status === 'failed' ? 'ice_failed' : 'viewer_left' }).catch(() => {});
      finishBroadcast(notice);
      return;
    }
    patch((v) => ({ broadcast: b, role: 'sharer', mine, active: null, everMine: v.everMine || mine }));
    stopTitleBlink(); stopChime();
    if (!mine) {
      // Otra pestaña mía tiene la captura... o esta es una recarga y la captura murió.
      if (source === 'boot' && !(await askSiblings(b.id))) {
        void api.post(`/api/share/${b.id}/end`, { reason: 'sharer_stopped' }).catch(() => {});
      }
      return;
    }
    // El share.broadcast puede ganarle a la respuesta del POST /invites (que trae los iceServers):
    // los peers se arman cuando llegan; startShare revisa al volver.
    if (iceRef.current.mintedAt === 0 || !captureRef.current) return;
    reconcileRef.current(b);
    if (source !== 'socket') void replaySignals(b.id);
  }, [askSiblings, finishBroadcast, myId, noticeForBroadcastEnd, patch, replaySignals, stopChime, stopTitleBlink]);

  const refreshState = useCallback(async (source: 'state' | 'boot' = 'state') => {
    if (!enabled) return;
    try {
      const st = await api.get<StateResponse>('/api/share/state');
      patch({ sessionCutAt: st.sessionCutAt, idleLimitMs: st.idleLimitMs ?? 0 });
      // Pendientes: las que el servidor ya no lista, vencieron.
      patch((v) => ({ pending: v.pending.filter((p) => st.pending.some((q) => q.id === p.id)) }));
      for (const p of st.pending) await applySession(p, source);
      // SHARER: mi broadcast.
      const curB = viewRef.current.broadcast;
      if (st.broadcast) await applyBroadcast(st.broadcast, source);
      else if (curB) finishBroadcast({ kind: 'lost' });
      // VIEWER: mi sesión.
      const cur = viewRef.current.active;
      if (st.active) await applySession(st.active, source);
      else if (cur) {
        // El servidor ya no tiene mi sesión viva (se cerró mientras el socket dormía). La ventana
        // propia también se entera acá: si el `share.ended` se perdió con el socket caído, sin esto
        // se quedaba con el último cuadro mandando `/alive` a una fila ya cerrada.
        finishSession(cur, { kind: 'lost', name: cur.sharer.name }, { lastFrame: true });
        if (windowMode) {
          quietCloseRef.current = true;
          setTimeout(() => { try { window.close(); } catch { /* */ } }, LAST_FRAME_MS);
        }
      }
    } catch { /* silencioso: el socket o el próximo tick lo reponen */ }
  }, [applyBroadcast, applySession, enabled, finishBroadcast, finishSession, patch, windowMode]);
  const refreshRef = useRef(refreshState);
  refreshRef.current = refreshState;

  /**
   * Salir (logout) o perder la habilitación con una sesión viva: se cierra en el SERVIDOR y se sueltan
   * captura y peer. Antes, al hacer Log out con un share en vivo, `me` quedaba null, se iban la píldora
   * y el socket… y la pantalla y el micrófono seguían saliendo al viewer por P2P hasta que el `/alive`
   * de 60 s devolvía 401 (spec §6.3: píldora permanente). Corre ANTES del POST /logout (`onBeforeLogout`,
   * con la cookie viva, `keepalive` por si el documento se va) y como red de seguridad cuando `enabled`
   * cae por otro camino. Lo que importa es el teardown local; el POST puede fallar.
   */
  const endMine = useCallback(async (opts: { keepalive?: boolean } = {}) => {
    const v = viewRef.current;
    let req: Promise<unknown> | null = null;
    if (v.broadcast && v.role === 'sharer') {
      req = api.post(`/api/share/${v.broadcast.id}/end`, { reason: 'sharer_stopped' }, opts).catch(() => {});
      finishBroadcast(null);
    } else if (v.active && v.role === 'viewer') {
      req = api.post(`/api/share/${v.active.id}/end`, { reason: 'viewer_left' }, opts).catch(() => {});
      finishSession(v.active, null);
    }
    if (v.pending.length) { patch({ pending: [] }); stopChime(); stopTitleBlink(); }
    if (req) await req;
  }, [finishBroadcast, finishSession, patch, stopChime, stopTitleBlink]);
  const endMineRef = useRef(endMine);
  endMineRef.current = endMine;
  useEffect(() => onBeforeLogout(() => endMineRef.current({ keepalive: true })), []);
  useEffect(() => { if (!enabled) void endMineRef.current(); }, [enabled]);

  // ───────────────────────── socket ─────────────────────────

  // `me` va en las dependencias a propósito: un /me nuevo (flag del inspector prendido o apagado,
  // reactivación) vuelve a llamar `rt.start()`, que es lo único que revive un socket `disabled`
  // (cierre 4403 `share_not_enabled` en el upgrade, spec §4.11). stop()+start() en el mismo tick no cierran el socket.
  useEffect(() => {
    if (!enabled) return undefined;
    armChime();
    rt.start();
    const offs = [
      rt.onStatus((s, info) => {
        patch({ socket: s, socketReconnecting: s !== 'open' && s !== 'disabled' && (info.reconnected || s === 'reconnecting') });
        // Resync SOLO si hay algo que reponer: los sockets sin sesión no hacen ningún GET al volver.
        if (s === 'open' && info.reconnected) {
          const v = viewRef.current;
          if (v.active || v.broadcast || v.pending.length) void refreshRef.current('state');
        }
      }),
      rt.on('share.invite', (m) => { void applySession(m.session, 'socket'); }),
      rt.on('share.update', (m) => { void applySession(m.session, 'socket'); }),
      rt.on('share.ended', (m) => { void applySession(m.session, 'socket'); }),
      // Malla: al SHARER le llega SU broadcast (roster/estado); reemplaza el share.update que consumía.
      rt.on('share.broadcast', (m) => { void applyBroadcast(m.broadcast, 'socket'); }),
      rt.on('share.signal', (m) => applySignal(m.signal)),
    ];
    return () => { for (const off of offs) off(); rt.stop(); };
  }, [applyBroadcast, applySession, applySignal, enabled, me, patch]);

  // Canal entre pestañas del MISMO navegador: "¿quién tiene viva la sesión X?" (ver askSiblings).
  useEffect(() => {
    if (!enabled || typeof BroadcastChannel === 'undefined') return undefined;
    const ch = new BroadcastChannel(CHANNEL);
    channelRef.current = ch;
    const onMsg = (e: MessageEvent) => {
      const d = e.data as { t?: string; sessionId?: number; nonce?: string };
      const v = viewRef.current;
      if (d?.t === 'share.who') {
        if (v.active && v.mine && v.active.id === d.sessionId) ch.postMessage({ t: 'share.alive', nonce: d.nonce });
        return;
      }
      if (d?.t === 'share.window-closed') {
        // La ventana propia se cerró con la X: esta pestaña (la que la abrió, con la píldora "Viewing
        // in a separate window") trae el video de vuelta en vez de dejar al sharer emitiendo a un
        // peer muerto hasta que ICE falle y la sesión termine como `ice_failed`.
        if (!windowMode && v.active && v.active.id === d.sessionId && v.role === 'viewer' && v.elsewhere && !v.mine && v.everMine && joiningRef.current !== d.sessionId) {
          bringBackRef.current();
        }
      }
    };
    ch.addEventListener('message', onMsg);
    return () => { ch.removeEventListener('message', onMsg); ch.close(); channelRef.current = null; };
  }, [enabled, windowMode]);

  // Arranque: una lectura de /state (recarga en medio de una sesión, push con ?share=<id>,
  // pestaña descartada). En la ventana propia el arranque es el handoff.
  useEffect(() => {
    if (!enabled || bootedRef.current) return;
    bootedRef.current = true;
    if (windowMode) {
      joiningRef.current = windowSessionId;
      // El clic que abrió la ventana es el gesto más reciente; si el navegador no lo cuenta, el botón
      // "Turn on my microphone" de los controles lo vuelve a pedir desde un clic propio.
      void requestViewerMic();
      void (async () => {
        try {
          const r = await api.post<HandoffResponse>(`/api/share/${windowSessionId}/handoff`, { tabId: TAB_ID, ua: navigator.userAgent, poppedOut: 'popup' });
          iceRef.current = { servers: r.iceServers, mintedAt: Date.now(), maxBitrateKbps: r.session.maxBitrateKbps };
          joiningRef.current = null;
          patch({ active: r.session, role: 'viewer', mine: true, elsewhere: false, everMine: true });
          syncViewerMicMuted(r.session);
          buildPeer(r.session);
          await replaySignals(r.session.id);
        } catch (e) {
          joiningRef.current = null;
          patch({ notice: e instanceof ApiError && e.code === 'gone' ? { kind: 'gone' } : errNotice(e) });
        }
      })();
      return;
    }
    // `?share=<id>` viene del push (sw.js enfoca la ventana con esa URL; en el inspector,
    // `/inspector?share=<id>`): se lee UNA vez, se limpia para no re-disparar al recargar, y la
    // tarjeta se abre solo si /state todavía la lista. Si ya venció, se avisa "gone".
    let deepLink: number | null = null;
    try {
      deepLink = parseShareDeepLink(window.location.search);
      if (deepLink != null) window.history.replaceState(null, '', stripShareDeepLink(window.location.href));
    } catch { /* */ }
    void (async () => {
      await refreshRef.current('boot');
      if (deepLink == null) return;
      const v = viewRef.current;
      if (!v.pending.some((p) => p.id === deepLink) && v.active?.id !== deepLink) patch({ notice: { kind: 'gone' } });
    })();
  }, [buildPeer, enabled, patch, replaySignals, requestViewerMic, windowMode, windowSessionId]);

  // Red de seguridad: con sesión pendiente/viva se relee /state cada 30 s (SELECT, cero writes).
  const hasSession = view.pending.length > 0 || view.active != null || view.broadcast != null;
  useEffect(() => {
    if (!enabled || !hasSession) return undefined;
    const id = window.setInterval(() => { void refreshRef.current('state'); }, STATE_EVERY_MS);
    return () => clearInterval(id);
  }, [enabled, hasSession]);

  // Web Lock mientras haya sesión: exención documentada del congelado por Energy Saver.
  useEffect(() => { rt.holdLock(enabled && hasSession); }, [enabled, hasSession]);

  // /alive cada 60 s mientras la sesión esté viva y sea MI pestaña (viewer: mi sesión; sharer: mi broadcast).
  const aliveOn = view.mine && (
    (view.active != null && (view.active.status === 'accepted' || view.active.status === 'live')) ||
    (view.broadcast != null && (view.broadcast.status === 'live' || view.broadcast.status === 'pending'))
  );
  const activeId = view.active?.id ?? view.broadcast?.id ?? null;
  useEffect(() => {
    if (!aliveOn || activeId == null) return undefined;
    let vivo = true;
    const tick = async () => {
      for (let intento = 0; vivo; intento += 1) {
        try {
          const r = await api.post<{ ok: true; status: string | null }>(`/api/share/${activeId}/alive`, {});
          // La fila ya no está viva (se cerró mientras el socket dormía): que /state lo aplique ya.
          if (r.status !== 'accepted' && r.status !== 'live') void refreshRef.current('state');
          return;
        } catch (e) {
          if (!isRetryable(e) || intento >= ALIVE_RETRY_MS.length) return;
          await sleep(ALIVE_RETRY_MS[intento]!);
        }
      }
    };
    const id = window.setInterval(() => { void tick(); }, ALIVE_EVERY_MS);
    return () => { vivo = false; clearInterval(id); };
  }, [aliveOn, activeId]);

  // La ventana propia se cerró con la X (o se colgó): el `pagehide` de una ventana que se está
  // destruyendo no siempre alcanza a hablar (medido en el e2e: ni BroadcastChannel ni `storage`
  // llegaron), así que la pestaña que la abrió la vigila con el mismo "¿quién tiene la sesión?" de
  // askSiblings. Dos preguntas sin respuesta (6 s) = la ventana ya no está: se trae el video de
  // vuelta en vez de dejar al sharer emitiendo a un peer muerto hasta que ICE falle.
  const watchingWindow = !windowMode && view.active != null && view.role === 'viewer' && view.elsewhere && !view.mine && view.everMine;
  const watchedId = view.active?.id ?? null;
  useEffect(() => {
    if (!watchingWindow || watchedId == null) return undefined;
    let faltas = 0;
    let vivo = true;
    const id = window.setInterval(() => {
      void (async () => {
        if (joiningRef.current === watchedId) return;
        const hay = await askSiblings(watchedId);
        if (!vivo) return;
        faltas = hay ? 0 : faltas + 1;
        if (faltas >= WINDOW_PING_MISSES) {
          const v = viewRef.current;
          if (v.active?.id === watchedId && v.elsewhere && !v.mine && joiningRef.current !== watchedId) bringBackRef.current();
          faltas = 0;
        }
      })();
    }, WINDOW_PING_MS);
    return () => { vivo = false; clearInterval(id); };
  }, [askSiblings, watchingWindow, watchedId]);

  // Sharer que cierra la pestaña: el stream muere con ella. `keepalive` para que el POST sobreviva
  // al unload (sin él Chromium lo cancela y el viewer se queda con el video congelado hasta que el
  // barrido cierra por `alive`, que sigue siendo la red de seguridad).
  // Viewer en la ventana propia que la cierra con la X: se avisa a la pestaña principal por el
  // BroadcastChannel para que traiga el video de vuelta. Un cierre a propósito (Leave, "Bring
  // back" desde la pestaña) no avisa: la sesión ya se resolvió por el servidor.
  // El viewer de la pestaña principal NO manda `viewer_left` al descargarse: la spec (§ pestañas)
  // pide que recargar recupere el video con un handoff, y `pagehide` no distingue recargar de cerrar.
  useEffect(() => {
    if (!enabled) return undefined;
    const onHide = () => {
      const v = viewRef.current;
      if (!v.mine) return;
      if (v.role === 'sharer' && v.broadcast) void api.post(`/api/share/${v.broadcast.id}/end`, { reason: 'sharer_left' }, { keepalive: true }).catch(() => {});
      else if (v.role === 'viewer' && v.active && windowMode && !quietCloseRef.current) channelRef.current?.postMessage({ t: 'share.window-closed', sessionId: v.active.id });
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, [enabled, windowMode]);

  // ── RED DE SEGURIDAD: el sonido arranca en el PRIMER gesto, sin buscar un botón (spec §4.10) ──
  // Tres bloqueos posibles tras conectar, NINGUNO con error a la vista, que dejaban a los dos "sin
  // escucharse" (reporte del dueño 2026-09-08): (viewer) el <video> quedó mudo por autoplay
  // (`audioBlocked`); (sharer) la voz del viewer no arrancó por autoplay (`hearBlocked`); o (sharer) el
  // AudioContext de la mezcla quedó suspendido y el track SALIENTE es silencio digital
  // (`mixerSuspended`). Mientras alguno siga, el primer pointerdown/keydown EN CUALQUIER PARTE lo
  // resuelve: reanuda el contexto (revive el MISMO track, sin renegociar) y re-reproduce el
  // <video>/<audio>. Al lograrlo se limpia el flag y el listener se quita solo. En la Chrome del e2e
  // (autoplay siempre permitido) los tres flags nacen en false y este efecto ni se instala.
  const needAudioGesture =
    (view.role === 'viewer' && view.mine && view.audioBlocked) ||
    (view.role === 'sharer' && (view.hearBlocked || view.mixerSuspended));
  useEffect(() => {
    if (!enabled || !needAudioGesture) return undefined;
    const onGesture = () => {
      const v = viewRef.current;
      if (v.role === 'sharer') {
        const cap = captureRef.current;
        if (cap) void cap.resumeAudio().then((running) => { if (running) patch({ mixerSuspended: false }); });
        // Malla: reintenta la voz de TODOS los viewers (el gesto destrabó el autoplay del documento).
        playAllRemoteAudio();
      } else if (v.role === 'viewer') {
        const el = videoRef.current;
        if (el) {
          el.muted = false;
          if (el.volume === 0) el.volume = 1;
          el.play().then(() => patch({ audioBlocked: false, viewerMuted: false, volume: el.volume })).catch(() => {});
        }
      }
    };
    window.addEventListener('pointerdown', onGesture, true);
    window.addEventListener('keydown', onGesture, true);
    return () => {
      window.removeEventListener('pointerdown', onGesture, true);
      window.removeEventListener('keydown', onGesture, true);
    };
  }, [enabled, needAudioGesture, patch, playAllRemoteAudio]);

  // ───────────────────────── tarjeta: seen + chime + título + Notification ─────────────────────────

  const firstPending = view.pending[0] ?? null;
  const firstPendingId = firstPending?.id ?? null;
  const firstPendingName = firstPending?.sharer.name ?? '';
  useEffect(() => {
    if (firstPendingId == null || windowMode) return undefined;
    const id = firstPendingId;
    const markSeen = () => {
      if (document.hidden || seenRef.current.has(id)) return;
      seenRef.current.add(id);
      void api.post(`/api/share/${id}/seen`, { tabId: TAB_ID }).catch(() => { seenRef.current.delete(id); });
    };
    markSeen();
    document.addEventListener('visibilitychange', markSeen);

    // Timbre: dos notas ahora y cada 10 s, tres veces. Si el navegador no deja, título + Notification.
    let count = 0;
    const ring = () => { count += 1; void playChime(); if (count >= 3) stopChime(); };
    ring();
    chimeRef.current = window.setInterval(ring, 10_000);

    const title = tRef.current('share.incomingTitle', { name: firstPendingName });
    const closeNotif = () => { try { notifRef.current?.close(); } catch { /* */ } notifRef.current = null; };
    // Notification del sistema SOLO con la pestaña oculta (spec §4.2): visible ya está la tarjeta, y
    // una notificación duplicada con `requireInteraction` se queda pegada en el escritorio.
    if (document.hidden) {
      closeNotif();
      notifRef.current = systemNotification(title, tRef.current('share.incomingHint'), `share-${id}`);
      const tr = titleRef.current;
      if (tr.timer == null) {
        tr.original = document.title;
        let on = false;
        tr.timer = window.setInterval(() => { on = !on; document.title = on ? title : tr.original; }, 1000);
      }
    }
    // Al volver a la pestaña se cierra la notificación: la tarjeta ya está delante.
    const onVisible = () => { if (!document.hidden) { stopTitleBlink(); closeNotif(); } };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', markSeen);
      document.removeEventListener('visibilitychange', onVisible);
      stopChime();
      stopTitleBlink();
      closeNotif(); // aceptada, rechazada, cancelada o vencida: no queda nada pegado en el escritorio
    };
  }, [firstPendingId, firstPendingName, stopChime, stopTitleBlink, windowMode]);

  // Vencimiento local de las pendientes (el servidor también avisa; esto evita una tarjeta zombie).
  // Sharer: broadcast en 'pending' (nadie aceptó todavía) = "Ringing"; se revisa su vencimiento local.
  const sharerRinging = view.role === 'sharer' && view.broadcast?.status === 'pending';
  useEffect(() => {
    if (!view.pending.length && !(view.active?.status === 'pending') && !sharerRinging) return undefined;
    const id = window.setInterval(() => {
      const now = Date.now();
      const v = viewRef.current;
      const alive = v.pending.filter((p) => Date.parse(p.expiresAt) > now);
      if (alive.length !== v.pending.length) patch({ pending: alive });
      if (v.active?.status === 'pending' && v.role === 'sharer' && Date.parse(v.active.expiresAt) + 3000 < now) {
        void refreshRef.current('state');
      }
      // El broadcast sigue 'pending' y su(s) invitación(es) ya vencieron localmente: que /state lo cierre.
      if (v.role === 'sharer' && v.broadcast?.status === 'pending') {
        const parts = v.broadcast.participants.filter((p) => p.status === 'pending' || p.status === 'accepted' || p.status === 'live');
        if (parts.length && parts.every((p) => Date.parse(p.expiresAt) + 3000 < now)) void refreshRef.current('state');
      }
    }, 1000);
    return () => clearInterval(id);
  }, [patch, sharerRinging, view.active?.status, view.pending.length]);

  // ───────────────────────── acciones del sharer ─────────────────────────

  const openPicker = useCallback((mode: 'start' | 'add' = 'start') => {
    if (!canShare) return;
    // 'add' solo tiene sentido con un broadcast MÍO en curso; si no hay, cae a 'start'.
    const v = viewRef.current;
    const canAdd = mode === 'add' && v.broadcast != null && v.role === 'sharer' && v.mine;
    patch({ pickerOpen: true, pickerMode: canAdd ? 'add' : 'start', macPerm: false, notice: null });
  }, [canShare, patch]);
  const closePicker = useCallback(() => patch({ pickerOpen: false, macPerm: false }), [patch]);

  /**
   * Invita a UN viewer más al broadcast en curso con la captura `cap` ya abierta (no re-pide
   * getDisplayMedia): lo usan el multi-select del start y "Add people". Devuelve `false` si el
   * broadcast está lleno (para cortar la tanda). Un `target_busy`/`target_not_enabled` deja aviso
   * pero no corta la tanda.
   */
  const inviteExtra = useCallback(async (cap: Capture, target: TargetRow): Promise<boolean> => {
    try {
      const invited = await api.post<InviteResponse>('/api/share/invites', {
        viewerUserId: target.id,
        clientKey: crypto.randomUUID(),
        tabId: TAB_ID,
        surface: cap.surface ?? undefined,
        audioMic: cap.mic === 'on',
        audioSystem: cap.hasSystemAudio,
        ua: navigator.userAgent,
      });
      iceRef.current = { servers: invited.iceServers, mintedAt: Date.now(), maxBitrateKbps: invited.maxBitrateKbps };
      patch({ broadcast: invited.broadcast });
      reconcileRef.current(invited.broadcast);
      return true;
    } catch (e) {
      const code = e instanceof ApiError ? e.code : null;
      if (code === 'broadcast_full') { patch({ notice: { kind: 'broadcastFull', max: viewRef.current.broadcast?.maxViewers ?? MAX_VIEWERS } }); return false; }
      if (code === 'target_busy') patch({ notice: { kind: 'busy', name: target.name } });
      else if (code === 'target_not_enabled') patch({ notice: { kind: 'notEnabled', name: target.name } });
      else patch({ notice: errNotice(e) });
      return true;
    }
  }, [patch]);

  /**
   * DEBE correr en el mismo tick del clic: `captureScreen` abre el picker nativo síncrono. `also`
   * son los otros seleccionados en el multi-select del picker: se invitan después de que exista el
   * broadcast, reusando la MISMA captura (no re-piden getDisplayMedia).
   */
  const startShare = useCallback((target: TargetRow, also: TargetRow[] = []) => {
    if (!canShare || viewRef.current.broadcast) return;
    // Primea el <audio> de la voz del viewer DENTRO del gesto del clic (ida y vuelta): crearlo y un
    // play() de calentamiento ahora deja autorizada su reproducción para cuando llegue el track, sin
    // que el sharer tenga que buscar "Tap to hear {name}" (spec §4.10). Sin fuente todavía puede
    // rechazar: no importa, es solo para gastar la activación del gesto.
    const a0 = remoteAudioEl('__warm__');
    if (a0) { try { a0.muted = false; void a0.play().catch(() => {}); } catch { /* */ } }
    let capPromise: Promise<Capture>;
    try { capPromise = captureScreen({ withMic: true }); }
    catch (e) { capPromise = Promise.reject(e); }
    void (async () => {
      let cap: Capture;
      try { cap = await capPromise; }
      catch (e) {
        const kind = e instanceof CaptureFailed ? e.kind : 'failed';
        if (kind === 'mac_permission') patch({ macPerm: true, pickerOpen: true });
        else if (kind === 'cancelled') patch({ notice: { kind: 'cancelledPicker' }, pickerOpen: false });
        else patch({ notice: { kind: 'error', message: e instanceof Error ? e.message : String(e) }, pickerOpen: false });
        return;
      }
      captureRef.current = cap;
      // Sin voz se comparte igual (spec §4.10): negado → "Microphone blocked"; sin dispositivo → "No microphone".
      const micNotice: Notice | null = cap.mic === 'denied' ? { kind: 'micDenied' } : cap.mic === 'none' ? { kind: 'noMic' } : null;
      // `mixerSuspended` = el AudioContext de la mezcla no llegó a 'running' (buildMixer ya esperó su
      // resume): el track saliente es silencio hasta que la red de seguridad lo reanude en un gesto.
      patch({ pickerOpen: false, macPerm: false, capture: { mic: cap.mic, hasSystemAudio: cap.hasSystemAudio, systemAudio: cap.systemAudio, muted: false }, notice: micNotice, mixerSuspended: cap.isAudioSuspended() });
      cap.onEnded(() => {
        // Barra nativa "Stop sharing" (o el sharer cerró la ventana compartida): cierra el broadcast entero.
        const b = viewRef.current.broadcast;
        if (b && viewRef.current.role === 'sharer' && captureRef.current === cap) {
          void api.post(`/api/share/${b.id}/end`, { reason: 'sharer_stopped' }).catch(() => {});
          finishBroadcast(null);
        } else if (captureRef.current === cap) {
          teardownCapture();
        }
      });
      let invited: InviteResponse;
      try {
        invited = await api.post<InviteResponse>('/api/share/invites', {
          viewerUserId: target.id,
          clientKey: crypto.randomUUID(),
          tabId: TAB_ID,
          surface: cap.surface ?? undefined,
          // [rev.3] qué audio va: voz y/o sistema (spec §4.10; reemplazan al `withAudio` de la rev.2).
          audioMic: cap.mic === 'on',
          audioSystem: cap.hasSystemAudio,
          ua: navigator.userAgent,
        });
      } catch (e) {
        teardownCapture();
        const code = e instanceof ApiError ? e.code : null;
        if (code === 'target_busy' || code === 'sharer_busy') patch({ notice: { kind: 'busy', name: target.name } });
        else if (code === 'target_not_enabled') patch({ notice: { kind: 'notEnabled', name: target.name } });
        else patch({ notice: errNotice(e) });
        return;
      }
      iceRef.current = { servers: invited.iceServers, mintedAt: Date.now(), maxBitrateKbps: invited.maxBitrateKbps };
      if (cap.videoTrack.readyState === 'ended') {
        // Apretó "Stop sharing" mientras viajaba el POST: nunca hubo nada que mostrar.
        void api.post(`/api/share/${invited.broadcast.id}/cancel`, {}).catch(() => {});
        teardownCapture();
        return;
      }
      // Si el share.broadcast{accepted} le ganó a esta respuesta, se usa el broadcast más nuevo.
      const already = viewRef.current.broadcast;
      const b = already && already.id === invited.broadcast.id ? already : invited.broadcast;
      patch((v) => ({ broadcast: b, role: 'sharer', mine: true, elsewhere: false, active: null, notice: invited.targetAppOpen ? v.notice : { kind: 'offlineSent', name: target.name } }));
      reconcileRef.current(b);
      // Multi-select: el resto de los elegidos entra al MISMO broadcast, sin re-pedir la captura.
      for (const extra of also) { if (!(await inviteExtra(cap, extra))) break; }
    })();
  }, [canShare, finishBroadcast, inviteExtra, patch, remoteAudioEl, teardownCapture]);

  /**
   * Agrega uno o varios viewers a MI broadcast en curso (spec §14): reusa la captura (NO hay
   * getDisplayMedia), así que no exige un gesto especial; por cada uno un `POST /invites` que agrega
   * un participante. `broadcast_full` corta con aviso; el resto se sigue invitando.
   */
  const addViewers = useCallback((targets: TargetRow[]) => {
    const cap = captureRef.current;
    if (!canShare || !viewRef.current.broadcast || viewRef.current.role !== 'sharer' || !viewRef.current.mine || !cap) return;
    patch({ pickerOpen: false });
    void (async () => { for (const target of targets) { if (!(await inviteExtra(cap, target))) break; } })();
  }, [canShare, inviteExtra, patch]);

  /** Quita a un viewer del broadcast (o cancela su invitación pendiente). El resto sigue intacto. */
  const removeViewer = useCallback((viewerId: string) => {
    const b = viewRef.current.broadcast;
    if (!b || viewRef.current.role !== 'sharer') return;
    const part = b.participants.find((p) => p.viewer.id === viewerId);
    if (!part) return;
    void api.post(`/api/share/${b.id}/participants/${part.id}/remove`, {}).catch(() => {});
    // Optimista: el share.broadcast lo confirma, pero cerramos el peer ya para no seguir emitiéndole.
    peersRef.current.get(viewerId)?.close();
    peersRef.current.delete(viewerId);
    peerMetaRef.current.delete(viewerId);
    connectReportedRef.current.delete(viewerId);
    dropAudioEl(viewerId);
    patch((v) => {
      const phases = { ...v.peerPhases }; delete phases[viewerId];
      const st = { ...v.peerStats }; delete st[viewerId];
      return { peerPhases: phases, peerStats: st };
    });
  }, [dropAudioEl, patch]);

  const cancelInvite = useCallback(() => {
    const b = viewRef.current.broadcast;
    if (!b || viewRef.current.role !== 'sharer') return;
    void api.post(`/api/share/${b.id}/cancel`, {}).catch(() => {});
    finishBroadcast(null);
  }, [finishBroadcast]);

  const stopSharing = useCallback(() => {
    const b = viewRef.current.broadcast;
    if (!b || viewRef.current.role !== 'sharer') return;
    if (b.status === 'pending') { cancelInvite(); return; }
    void api.post(`/api/share/${b.id}/end`, { reason: 'sharer_stopped' }).catch(() => {});
    finishBroadcast(null);
  }, [cancelInvite, finishBroadcast]);

  /**
   * Mute del sharer (spec §4.10): silencio LOCAL al instante (track del mic `enabled=false` + ganancia
   * 0, nunca se para el track) y `POST /:id/mute` para que el viewer pinte "Muted by Ana" con el
   * `micMuted` del `share.update`. Si el POST falla, el silencio local igual vale: lo que importa es
   * que no salga la voz.
   */
  const toggleMute = useCallback(() => {
    const cap = captureRef.current;
    if (!cap) return;
    const m = !cap.isMuted();
    cap.setMuted(m);
    patch((v) => ({ capture: v.capture ? { ...v.capture, muted: m } : null }));
    // Mute del sharer = a TODOS los viewers (spec §14). El servidor lo propaga por share.update a cada uno.
    const b = viewRef.current.broadcast;
    if (b && viewRef.current.role === 'sharer') void api.post(`/api/share/${b.id}/mute`, { muted: m, side: 'sharer' }).catch(() => {});
  }, [patch]);

  /** "Tap to hear {name}" del sharer: el clic es el gesto que el autoplay del <audio> exigía (a todos). */
  const hearViewer = useCallback(() => { playAllRemoteAudio(); }, [playAllRemoteAudio]);

  /**
   * Sharer: reanuda el audio SALIENTE cuando el AudioContext de la mezcla quedó suspendido (el que
   * mira no lo oía y no había ningún error). El clic es el gesto que faltaba: el MISMO track vuelve a
   * llevar señal al correr el contexto, sin renegociar. La red de seguridad hace lo mismo con cualquier
   * clic; este es el botón explícito.
   */
  const resumeAudio = useCallback(() => {
    const cap = captureRef.current;
    if (!cap) return;
    void cap.resumeAudio().then((running) => { if (running) patch({ mixerSuspended: false }); });
  }, [patch]);

  const setHint = useCallback((h: 'detail' | 'motion') => {
    peerRef.current?.setContentHint(h);
    patch({ hint: h });
  }, [patch]);

  // ───────────────────────── acciones del viewer ─────────────────────────

  const accept = useCallback((id: number) => {
    const inv = viewRef.current.pending.find((p) => p.id === id);
    if (!inv) return;
    // El clic en Accept es el gesto: el <video> arranca con sonido desde acá, y el micrófono propio se
    // pide en el MISMO tick (ida y vuelta): un `await` antes y Safari lo niega sin preguntar. El play()
    // de calentamiento gasta la activación del gesto ahora (todavía sin srcObject puede rechazar, no
    // importa) para que el play() real de `playRemote`, varios awaits después, ya quede autorizado.
    const v = videoRef.current;
    if (v) { v.muted = false; try { void v.play().catch(() => {}); } catch { /* */ } patch({ viewerMuted: false, audioBlocked: false }); }
    stopChime(); stopTitleBlink();
    joiningRef.current = id;
    void requestViewerMic();
    void (async () => {
      let r: AcceptResponse | null = null;
      // Un 502 del swap de un deploy: se reintenta a 1 s y 3 s; el UPDATE es idempotente por tabId.
      for (const wait of [0, 1000, 3000]) {
        if (wait) await sleep(wait);
        try { r = await api.post<AcceptResponse>(`/api/share/${id}/accept`, { tabId: TAB_ID, ua: navigator.userAgent }); break; }
        catch (e) {
          if (e instanceof ApiError && e.status === 409) { joiningRef.current = null; teardownViewerMic(); patch((vv) => ({ pending: vv.pending.filter((p) => p.id !== id), notice: { kind: 'gone' } })); return; }
          if (!isRetryable(e)) { joiningRef.current = null; teardownViewerMic(); patch({ notice: errNotice(e) }); return; }
        }
      }
      if (!r) { joiningRef.current = null; teardownViewerMic(); patch({ notice: { kind: 'error' } }); return; }
      iceRef.current = { servers: r.iceServers, mintedAt: Date.now(), maxBitrateKbps: r.maxBitrateKbps };
      joiningRef.current = null;
      patch((vv) => ({ active: r!.session, role: 'viewer', mine: true, elsewhere: false, everMine: true, pending: vv.pending.filter((p) => p.id !== id), notice: null }));
      syncViewerMicMuted(r.session);
      buildPeer(r.session);
      await replaySignals(r.session.id);
    })();
  }, [buildPeer, patch, replaySignals, requestViewerMic, stopChime, stopTitleBlink, syncViewerMicMuted, teardownViewerMic]);

  const decline = useCallback((id: number) => {
    const inv = viewRef.current.pending.find((p) => p.id === id);
    if (inv) cooldownRef.current.set(inv.sharer.id, Date.now() + DECLINE_COOLDOWN_MS);
    patch((v) => ({ pending: v.pending.filter((p) => p.id !== id) }));
    void api.post(`/api/share/${id}/decline`, {}).catch(() => {});
  }, [patch]);

  const leave = useCallback(() => {
    const cur = viewRef.current.active;
    if (!cur) return;
    void api.post(`/api/share/${cur.id}/end`, { reason: viewRef.current.role === 'sharer' ? 'sharer_stopped' : 'viewer_left' }).catch(() => {});
    finishSession(cur, null);
    if (windowMode) { quietCloseRef.current = true; setTimeout(() => { try { window.close(); } catch { /* */ } }, 300); }
  }, [finishSession, windowMode]);

  /** Traer el video de vuelta a ESTA pestaña (desde la ventana propia u otra pestaña). */
  const bringBack = useCallback(() => {
    const cur = viewRef.current.active;
    if (!cur || viewRef.current.role !== 'viewer') return;
    joiningRef.current = cur.id;
    // "Bring back" es un clic: el micrófono vuelve con el video (la ventana que lo tenía lo soltó).
    void requestViewerMic();
    void (async () => {
      try {
        const r = await api.post<HandoffResponse>(`/api/share/${cur.id}/handoff`, { tabId: TAB_ID, ua: navigator.userAgent });
        iceRef.current = { servers: r.iceServers, mintedAt: Date.now(), maxBitrateKbps: r.session.maxBitrateKbps };
        joiningRef.current = null;
        patch({ active: r.session, mine: true, elsewhere: false, everMine: true, notice: null });
        syncViewerMicMuted(r.session);
        buildPeer(r.session);
        await replaySignals(r.session.id);
      } catch (e) {
        joiningRef.current = null;
        patch({ notice: e instanceof ApiError && e.code === 'gone' ? { kind: 'gone' } : errNotice(e) });
      }
    })();
  }, [buildPeer, patch, replaySignals, requestViewerMic, syncViewerMicMuted]);
  bringBackRef.current = bringBack;

  /** Ventana propia (camino universal, los cinco navegadores): `noopener` siempre; el handoff va por el servidor. */
  const openInWindow = useCallback(() => {
    const cur = viewRef.current.active;
    if (!cur) return;
    const url = `/share/view/${cur.id}`;
    try { window.open(url, `ppag-share-${cur.id}`, 'popup,noopener,width=1280,height=760'); }
    catch { /* se detecta abajo por ausencia de handoff */ }
    // Con `noopener` el navegador devuelve null siempre: bloqueado o no se sabe por el retorno.
    // Se sabe por el servidor: si en 5 s no llegó share.update con otro viewerTabId, no abrió.
    if (popupWaitRef.current != null) clearTimeout(popupWaitRef.current);
    popupWaitRef.current = window.setTimeout(() => {
      popupWaitRef.current = null;
      const v = viewRef.current;
      if (v.active && v.active.id === cur.id && v.mine) patch({ notice: { kind: 'popupBlocked' } });
    }, POPUP_HANDOFF_WAIT_MS);
  }, [patch]);

  /**
   * Pop out = Document PiP (Chromium 116+, Firefox 151+ con la pref). Si `requestWindow` rechaza
   * (pref apagada, sin gesto, política) se cae a "Open in a window" SIN error visible (spec §4.6).
   * Requiere gesto: llamar desde el clic.
   */
  const popOut = useCallback(async () => {
    const v = videoRef.current;
    const cur = viewRef.current.active;
    if (!v || !cur) return;
    if (docPipSupported()) {
      const h = await openDocPip(v, { enlarge: t('share.enlarge'), dragCorner: t('share.dragCorner'), title: t('share.screenOf', { name: cur.sharer.name }) }, () => {
        docPipRef.current = null;
        patch({ poppedOut: null });
        // El stage lo vuelve a montar al ver poppedOut=null (efecto en ShareStage).
      });
      if (h) {
        docPipRef.current = h;
        patch({ poppedOut: 'doc_pip' });
        return;
      }
    }
    openInWindow();
  }, [openInWindow, patch, t]);

  /**
   * PiP clásico del <video> (Safari 18+, Chromium; Firefox 153+ por API). `requestVideoPip` aplica
   * el workaround de WebKit 262479 y COMPRUEBA `document.pictureInPictureElement`: si el navegador
   * dijo que sí pero no abrió nada, se cae a "Open in a window".
   */
  const popOutVideo = useCallback(async () => {
    const v = videoRef.current;
    const cur = viewRef.current.active;
    if (!v || !cur) return;
    if (await requestVideoPip(v)) {
      patch({ poppedOut: 'video_pip' });
      const back = () => { v.removeEventListener('leavepictureinpicture', back); patch({ poppedOut: null }); };
      v.addEventListener('leavepictureinpicture', back);
      return;
    }
    openInWindow();
  }, [openInWindow, patch]);

  const setMinimized = useCallback((m: boolean) => patch({ minimized: m }), [patch]);

  const setViewerMuted = useCallback((m: boolean) => {
    const v = videoRef.current;
    if (v) v.muted = m;
    patch({ viewerMuted: m });
  }, [patch]);

  /** Volumen local del viewer (0-1). A 0 se muestra como silenciado; subirlo lo destapa. */
  const setVolume = useCallback((vol: number) => {
    const n = Math.min(1, Math.max(0, Number.isFinite(vol) ? vol : 1));
    const v = videoRef.current;
    if (v) { v.volume = n; v.muted = n === 0; }
    patch({ volume: n, viewerMuted: n === 0 });
  }, [patch]);

  /** "Tap to hear audio": el clic es el gesto que el autoplay exigía. */
  const unmute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    if (v.volume === 0) v.volume = 1;
    v.play().then(() => patch({ audioBlocked: false, viewerMuted: false, volume: v.volume })).catch(() => { v.muted = true; });
  }, [patch]);

  /**
   * Mute del VIEWER sobre su propio micrófono (ida y vuelta): silencio local al instante
   * (`track.enabled=false`, nunca se para) y `POST /:id/mute { side: 'viewer' }` para que el sharer
   * pinte "{name} muted their microphone" con el `viewerMicMuted` del `share.update`.
   */
  const toggleViewerMic = useCallback(() => {
    const ms = micStreamRef.current;
    if (!ms) return;
    const m = !viewRef.current.viewerMicMuted;
    for (const t of ms.getAudioTracks()) t.enabled = !m;
    patch({ viewerMicMuted: m });
    const cur = viewRef.current.active;
    if (cur && viewRef.current.role === 'viewer') void api.post(`/api/share/${cur.id}/mute`, { muted: m, side: 'viewer' }).catch(() => {});
  }, [patch]);

  /** "Turn on my microphone": el clic es el gesto (Safari) o el segundo intento tras un permiso negado. */
  const turnOnMic = useCallback(() => { void requestViewerMic(); }, [requestViewerMic]);

  const dismissNotice = useCallback(() => patch({ notice: null }), [patch]);
  const cooldownUntil = useCallback((userId: string) => cooldownRef.current.get(userId) ?? 0, []);
  const refreshPublic = useCallback(() => { void refreshRef.current('state'); }, []);

  const value = useMemo<ShareContext>(() => ({
    ...view, enabled, canShare, canView, fieldMode, windowMode, myId, videoEl: videoRef.current,
    openPicker, closePicker, startShare, addViewers, removeViewer, cancelInvite, stopSharing, toggleMute, accept, decline, leave, bringBack,
    openInWindow, popOut, popOutVideo, setHint, setMinimized, setViewerMuted, setVolume, unmute, toggleViewerMic, turnOnMic, hearViewer, resumeAudio, dismissNotice, refreshState: refreshPublic, cooldownUntil,
  }), [view, enabled, canShare, canView, fieldMode, windowMode, myId, openPicker, closePicker, startShare, addViewers, removeViewer, cancelInvite, stopSharing, toggleMute, accept, decline, leave, bringBack, openInWindow, popOut, popOutVideo, setHint, setMinimized, setViewerMuted, setVolume, unmute, toggleViewerMic, turnOnMic, hearViewer, resumeAudio, dismissNotice, refreshPublic, cooldownUntil]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {enabled && canShare && !windowMode && view.pickerOpen && <ShareTargetPicker />}
      {enabled && !windowMode && view.pending.map((p) => <ShareRequestCard key={p.id} session={p} />)}
      {enabled && view.active && view.role === 'viewer' && (view.mine || view.elsewhere) && <ShareStage />}
      {enabled && !windowMode && view.broadcast && view.role === 'sharer' && <SharerPill />}
      {enabled && !windowMode && view.notice && !view.active && !view.broadcast && !view.pickerOpen && <ShareNoticeToast />}
    </Ctx.Provider>
  );
}

export function useShare(): ShareContext {
  const c = useContext(Ctx);
  if (!c) throw new Error('useShare fuera de ShareProvider');
  return c;
}

/** ¿Este navegador puede compartir? (los teléfonos no tienen getDisplayMedia) */
export function canShareHere(): boolean { return displayMediaSupported(); }

/** Aviso transitorio del sharer fuera de sesión (rechazó, no contestó, se perdió...). */
function ShareNoticeToast() {
  const s = useShare();
  const { t } = useI18n();
  const n = s.notice;
  useEffect(() => {
    if (!n) return undefined;
    const id = window.setTimeout(() => s.dismissNotice(), n.kind === 'lost' ? 20_000 : 8_000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n]);
  if (!n) return null;
  const text = noticeText(n, t);
  return (
    <div className="ss-toast" role="status" onClick={s.dismissNotice}>
      <span>{text}</span>
      {n.kind === 'lost' && n.again && s.canShare && (
        <button type="button" className="btn sm" onClick={(e) => { e.stopPropagation(); s.dismissNotice(); s.startShare(n.again!); }}>
          {t('share.again', { name: n.again.name })}
        </button>
      )}
    </div>
  );
}

export function noticeText(n: Notice, t: (k: string, vars?: Record<string, string | number>) => string): string {
  const name = n.name ?? '';
  switch (n.kind) {
    case 'declined': return t('share.declined', { name });
    case 'noAnswer': return t('share.noAnswer', { name });
    case 'busy': return t('share.busy', { name });
    case 'notEnabled': return t('share.notEnabled', { name });
    case 'offlineSent': return t('share.offlineSent', { name });
    case 'endedBy': return t('share.endedBy', { name });
    case 'viewerLeft': return t('share.viewerLeft', { name });
    case 'lost': return t('share.lost', { name });
    case 'cancelledPicker': return t('share.cancelledPicker');
    case 'gone': return t('share.gone');
    case 'answeredElsewhere': return t('share.answeredElsewhere');
    case 'popupBlocked': return t('share.popupBlocked');
    case 'micDenied': return t('share.micDenied');
    case 'noMic': return t('share.noMic');
    case 'movedBack': return t('share.movedBack');
    case 'rateLimited': return t('share.rateLimited');
    case 'notEnabledMe': return t('share.notEnabledMe');
    case 'broadcastFull': return t('share.broadcastFull', { max: n.max ?? MAX_VIEWERS });
    // Solo un mensaje LEGIBLE (errNotice ya filtró los códigos tipo `http_502`).
    case 'error': return n.message || t('share.error');
    default: return '';
  }
}
